import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useLazyQuery,
} from 'react-apollo';
import type { GraphQLError } from 'graphql';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { useCallback } from 'react';

import { usePageSpaceKey } from '@confluence/page-context';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';

import {
	StarredSpacesQuery,
	MyVisitedSpacesQuery,
	MyVisitedSpacesWithCurrentSpaceQuery,
	AnonymousSpacesQuery,
	SearchSpacesQuery,
} from './SpacesListQueries.graphql.ts';
import type { SpacesListSpaceFragment as SpaceFragment } from './__types__/SpacesListSpaceFragment.ts';
import type { StarredSpacesQuery as StarredResult } from './__types__/StarredSpacesQuery.ts';
import type { AnonymousSpacesQuery as AnonymousResult } from './__types__/AnonymousSpacesQuery.ts';
import type {
	SearchSpacesQuery as SearchSpacesQueryResult,
	SearchSpacesQueryVariables,
} from './__types__/SearchSpacesQuery.ts';
import type {
	MyVisitedSpacesWithCurrentSpaceQuery as VisitedWithCurrentResult,
	MyVisitedSpacesWithCurrentSpaceQueryVariables as VisitedWithCurrentVars,
} from './__types__/MyVisitedSpacesWithCurrentSpaceQuery.ts';
import type { MyVisitedSpacesQuery as VisitedResult } from './__types__/MyVisitedSpacesQuery.ts';

export type Space = SpaceFragment & {
	currentUser?: {
		isFavourited: boolean;
	};
};

type SearchedSpace = SpaceFragment & {
	currentUser: {
		isFavourited: boolean;
	};
};

const hasCurrentSpace = (
	data: VisitedResult | VisitedWithCurrentResult,
): data is VisitedWithCurrentResult => 'space' in data;

const isCurrentSpaceNotFoundError = (gqlError: GraphQLError) =>
	gqlError.path?.[0] === 'space' && gqlError.extensions?.['statusCode'] === 404;

const processStarredError = (error?: ApolloErrorInterop): ApolloErrorInterop | undefined => {
	if (!error) {
		return undefined;
	}

	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
		return undefined;
	}

	return error;
};

const processRecentError = (error?: ApolloErrorInterop): ApolloErrorInterop | undefined => {
	if (!error) {
		return undefined;
	}

	/**
	 * space
	 *    403: Expected to occur when the user is on a restricted space
	 *    404: Expected to occur when the user is on an invalid space
	 * recentSpaces
	 *    403: Harmless error temporarily being ignored to mitigate CONFDEV-71458.
	 *         Removing in CONFDEV-71656.
	 */
	if (
		isUnauthorizedError(error) ||
		(!error.networkError && error.graphQLErrors.every(isCurrentSpaceNotFoundError))
	) {
		markErrorAsHandled(error);
		return undefined;
	}

	return error;
};

export const useStarredData = (isLicensed: boolean) => {
	const query = isLicensed ? StarredSpacesQuery : AnonymousSpacesQuery;

	const { data, loading, error } = useQuery<StarredResult | AnonymousResult>(query, {
		variables: {},
	});

	const starredSpaces: Space[] = (data?.spaces?.nodes || [])
		.filter((space): space is Space => space != null)
		.map((space) => ({
			...space,
			currentUser: {
				isFavourited: isLicensed,
			},
		}));
	const unexpectedError = processStarredError(error);

	return {
		starredSpaces,
		loading,
		error: unexpectedError,
	};
};

const useRecentData = (isLicensed: boolean) => {
	const [spaceKey] = usePageSpaceKey();

	const VisitedQuery = spaceKey ? MyVisitedSpacesWithCurrentSpaceQuery : MyVisitedSpacesQuery;

	const { data, loading, error } = useQuery<
		VisitedResult | VisitedWithCurrentResult,
		VisitedWithCurrentVars
	>(VisitedQuery, {
		skip: !isLicensed,
		variables: { selectedSpaceKey: spaceKey },
		errorPolicy: 'all',
	});

	const currentSpace: Space | undefined =
		(data && hasCurrentSpace(data) && data.space) || undefined;

	const recentData = (data as VisitedResult)?.myVisitedSpaces?.nodes?.spaces;

	const recentSpaces: Space[] = (recentData || []).filter(
		(space): space is Space => space != null && (!currentSpace || currentSpace.id !== space.id),
	);

	const unexpectedError = processRecentError(error);

	return {
		recentSpaces,
		currentSpace,
		loading,
		error: unexpectedError,
	};
};

export const useSpacesData = () => {
	const { isLicensed } = useSessionData();

	const {
		starredSpaces,
		loading: starredLoading,
		error: starredError,
	} = useStarredData(isLicensed);

	const {
		recentSpaces: unfilteredRecents,
		currentSpace,
		loading: recentLoading,
		error: recentError,
	} = useRecentData(isLicensed);

	const starredIds = new Set(starredSpaces.map((starred) => starred.id));
	const recentSpaces = unfilteredRecents.filter((recent) => !starredIds.has(recent.id)).slice(0, 5);

	const errors: ApolloErrorInterop[] = [];
	if (starredError) errors.push(starredError);
	if (recentError) errors.push(recentError);

	return {
		errors,
		loading: starredLoading || recentLoading,
		starredSpaces,
		recentSpaces,
		currentSpace,
	};
};

export const useSpaceSearchData = () => {
	const isLicensed = useIsLicensed();

	const [executeSearch, { data, loading, error }] = useLazyQuery<
		SearchSpacesQueryResult,
		SearchSpacesQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SearchSpacesQuery,
	);

	const spaceSearchResults: SearchedSpace[] = (data?.spaces?.nodes || []).filter(
		(space): space is SearchedSpace => space != null,
	);

	const searchSpaces = useCallback(
		(pattern: string) => {
			if (pattern) {
				executeSearch({
					variables: {
						spaceNamePattern: pattern,
						isLicensed,
					},
				});
			}
		},
		[executeSearch, isLicensed],
	);

	return {
		spaceSearchResults,
		loading,
		error,
		searchSpaces,
	};
};
